import { useMemo } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { typography, semantic, radius } from '@/shared/theme/tokens';
import { SlideModal } from '@/shared/ui/SlideModal';
import { SEVERITY_META, ALERT_CATEGORY_META, catBg, sevBg } from '@/features/alerts/alertMeta';
import { formatTs, timeAgo, fmt2 } from '@/shared/lib/formatters';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

function fmtMins(mins) {
  if (mins == null) return null;
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

// Optimal band + critical ticks + current-value marker on a single track.
function BandBar({ alert, isDark }) {
  const { optimalMin: lo, optimalMax: hi, criticalMin: clo, criticalMax: chi, value: v } = alert;
  const pts = [lo, hi, clo, chi, v].filter((n) => n != null);
  if (pts.length < 2) return null;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const span = max - min || 1;
  const pct = (n) => `${((n - min) / span) * 100}%`;
  const sev = SEVERITY_META[alert.severity] ?? SEVERITY_META.warning;

  return (
    <div style={{ position: 'relative', height: 10, borderRadius: radius.sm, background: isDark ? 'rgba(255,255,255,0.06)' : '#eef2f7', marginTop: 4 }}>
      {lo != null && hi != null && (
        <div style={{
          position: 'absolute', top: 0, bottom: 0, left: pct(lo), right: `calc(100% - ${pct(hi)})`,
          background: semantic.successGreenBorder, borderRadius: radius.sm,
        }} />
      )}
      {v != null && (
        <div style={{
          position: 'absolute', top: -3, bottom: -3, left: pct(v), width: 3, marginLeft: -1.5,
          background: sev.color, borderRadius: radius.xs,
        }} title={`${fmt2(v)}${alert.unit ? ` ${alert.unit}` : ''}`} />
      )}
    </div>
  );
}

function TrendChart({ alert, c }) {
  const sev = SEVERITY_META[alert.severity] ?? SEVERITY_META.warning;
  const data = useMemo(() => {
    const series = Array.isArray(alert.series) ? alert.series : [];
    const forecast = Array.isArray(alert.forecastSeries) ? alert.forecastSeries : [];
    const labels = [...series, ...forecast].map((_, i) => i);
    const histed = series.concat(forecast.map(() => null));
    const fore = series.map(() => null);
    if (forecast.length) { fore[series.length - 1] = series[series.length - 1]; fore.push(...forecast); }
    return {
      labels,
      datasets: [
        { data: histed, borderColor: sev.color, borderWidth: 2, pointRadius: 0, tension: 0.35, fill: false },
        ...(forecast.length ? [{ data: fore, borderColor: semantic.violet, borderDash: [4, 3], borderWidth: 2, pointRadius: 0, tension: 0.35, fill: false }] : []),
      ],
    };
  }, [alert, sev.color]);

  const options = useMemo(() => ({
    responsive: true, maintainAspectRatio: false, animation: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: { x: { display: false }, y: { display: true, ticks: { color: c.textSubtle, font: { size: 10 } }, grid: { color: c.border } } },
  }), [c]);

  return <div style={{ height: 140 }}><Line data={data} options={options} /></div>;
}

/**
 * In-place alert detail for Live Mode. A page-local twin of the real alerts-page
 * modal (which is inline in that page and can't be imported here). Renders from
 * the alert object's own fields — no extra fetch, no existing page touched.
 */
export function AlertDetailDrawer({ alert, isOpen, onClose, onExited }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  if (!alert) return null;

  const catMeta = ALERT_CATEGORY_META[alert.category] ?? {};
  const sev = SEVERITY_META[alert.severity] ?? SEVERITY_META.warning;
  const offLabel = fmtMins(alert.offBandMins);

  return (
    <SlideModal isOpen={isOpen} onClose={onClose} onExited={onExited} maxWidth={560} c={c} isDark={isDark}>
      <div style={{ padding: 24 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 22, color: catMeta.color, flexShrink: 0, marginTop: 2 }}>
            {catMeta.icon}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: c.textPrimary, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={alert.parameterName}>
              {alert.parameterName}
            </div>
            <div style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, marginTop: 2 }}>{alert.layerName ?? alert.dataKey}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, padding: '2px 7px', borderRadius: radius.sm, background: catBg(catMeta, isDark), color: catMeta.color, border: `1px solid ${catMeta.border}` }}>
              {catMeta.label}
            </span>
            <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, padding: '2px 7px', borderRadius: radius.sm, background: sevBg(sev, isDark), color: sev.color, border: `1px solid ${sev.border}` }}>
              {sev.label}
            </span>
          </div>
          <button onClick={onClose} title="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textSubtle, padding: 4, borderRadius: radius.sm, flexShrink: 0 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
          </button>
        </div>

        {/* Reading + off-since */}
        <div style={{ display: 'flex', gap: 28, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, marginBottom: 6 }}>
              {alert.category === 'stale' ? 'Last reading' : 'Current reading'}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: typography.fontSize['7xl'], fontWeight: typography.fontWeight.bold, color: sev.color, fontVariantNumeric: 'tabular-nums' }}>
                {alert.value != null ? fmt2(alert.value) : '—'}
              </span>
              <span style={{ fontSize: typography.fontSize.lg, color: c.textSubtle }}>{alert.unit ?? ''}</span>
            </div>
          </div>
          {offLabel && (
            <div>
              <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, marginBottom: 6 }}>Off since</div>
              <div style={{ fontSize: typography.fontSize['7xl'], fontWeight: typography.fontWeight.bold, color: sev.color, fontVariantNumeric: 'tabular-nums' }}>{offLabel}</div>
            </div>
          )}
          {alert.lastReading && (
            <div>
              <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, marginBottom: 6 }}>Last update</div>
              <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: c.textPrimary }}>{timeAgo(alert.lastReading)}</div>
              <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, marginTop: 2 }}>{formatTs(alert.lastReading)}</div>
            </div>
          )}
        </div>

        <BandBar alert={alert} isDark={isDark} />

        {/* Predictive forecast chip */}
        {(alert.forecastSeries ?? []).length > 0 && (
          <div style={{ marginTop: 12, fontSize: typography.fontSize.sm, color: semantic.violet, padding: '8px 12px', borderRadius: radius.md, background: isDark ? 'rgba(124,58,237,0.12)' : 'rgba(124,58,237,0.07)', border: `1px solid ${semantic.violetBorder}`, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>schedule</span>
            Forecast to breach the optimal range soon.
          </div>
        )}

        {/* Trend */}
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, marginBottom: 8 }}>
            Parameter trend{(alert.forecastSeries ?? []).length ? ' + forecast' : ''}
          </div>
          <TrendChart alert={alert} c={c} />
        </div>

        {/* Linked recommendation CTA */}
        {alert.recId ? (
          <a
            href={`/deployments/${alert.deploymentId}/recommendations?rec=${alert.recId}`}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', borderRadius: radius.lg, textDecoration: 'none',
              background: isDark ? 'rgba(30,67,150,0.22)' : 'rgba(30,67,150,0.07)',
              border: `1px solid ${semantic.brandBorder}`,
              color: isDark ? '#93c5fd' : semantic.brandNavyMid, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>lightbulb</span>
              View linked recommendation
            </span>
            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>open_in_new</span>
          </a>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', borderRadius: radius.lg,
            background: isDark ? 'rgba(255,255,255,0.03)' : c.bgSubtle, border: `1px solid ${c.border}`,
            fontSize: typography.fontSize.base, color: c.textSubtle,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, opacity: 0.4 }}>lightbulb</span>
            No linked recommendation
          </div>
        )}
      </div>
    </SlideModal>
  );
}
